/**
 * ─── HRIS Attendance Records ────────────────────────────────
 * Time & attendance tracking with check-in/check-out,
 * overtime, late/undertime, and biometric source tagging.
 */

import { useEffect, useState } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { Clock, Plus, UserCheck, AlertTriangle, Calendar } from 'lucide-react'
import { employeeRepo } from '../../lib/db'

interface AttendanceEntry {
  id: string
  employeeId: string
  employeeName: string
  date: string
  checkIn: string
  checkOut: string
  status: 'present' | 'late' | 'absent' | 'half_day' | 'on_leave'
  source: 'biometric' | 'mobile' | 'manual'
  notes: string
  hoursWorked: number
  overtimeHours: number
}

const STORAGE_KEY = 'hris_attendance_records'

function loadRecords(): AttendanceEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function saveRecords(records: AttendanceEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records))
}

const STATUS_COLORS: Record<string, string> = {
  present: 'green', late: 'yellow', absent: 'red', half_day: 'blue', on_leave: 'purple',
}

export function HrisAttendancePage() {
  const [records, setRecords] = useState<AttendanceEntry[]>(loadRecords)
  const [employees, setEmployees] = useState<any[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [filterDate, setFilterDate] = useState('')
  const [form, setForm] = useState({
    employeeId: '', employeeName: '', date: new Date().toISOString().slice(0, 10),
    checkIn: '08:00', checkOut: '17:00', status: 'present' as string, source: 'manual' as string,
    notes: '',
  })

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeRepo.findMany({ page: 1, pageSize: 500 })
        setEmployees('items' in res ? res.items : [])
      } catch (e) { console.error(e) }
    })()
  }, [])

  const persist = (recs: AttendanceEntry[]) => {
    setRecords(recs)
    saveRecords(recs)
  }

  function handleAdd() {
    if (!form.employeeId) return
    const checkInTime = new Date(`${form.date}T${form.checkIn}:00`).getTime()
    const checkOutTime = new Date(`${form.date}T${form.checkOut}:00`).getTime()
    const hoursWorked = Math.max(0, (checkOutTime - checkInTime) / 3600000)
    const overtimeHours = Math.max(0, hoursWorked - 9)

    const selectedEmp = employees.find((e: any) => e.id === form.employeeId)
    const entry: AttendanceEntry = {
      id: 'att-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      ...form,
      employeeName: form.employeeName || (selectedEmp as any)?.fullName || (selectedEmp as any)?.name || form.employeeId,
      hoursWorked: Math.round(hoursWorked * 100) / 100,
      overtimeHours: Math.round(overtimeHours * 100) / 100,
    } as AttendanceEntry
    persist([entry, ...records])
    setShowAdd(false)
    setForm({ employeeId: '', employeeName: '', date: new Date().toISOString().slice(0, 10),
      checkIn: '08:00', checkOut: '17:00', status: 'present', source: 'manual', notes: '' })
  }

  const filtered = filterDate ? records.filter(r => r.date === filterDate) : records

  const today = new Date().toISOString().slice(0, 10)
  const todayRecords = records.filter(r => r.date === today)
  const presentToday = todayRecords.filter(r => r.status === 'present' || r.status === 'late').length
  const lateToday = todayRecords.filter(r => r.status === 'late').length
  const absentToday = todayRecords.filter(r => r.status === 'absent').length

  return (
    <div className="p-6">
      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-green-600" />
            <p className="text-xs text-green-600">Present Today</p>
          </div>
          <p className="mt-1 text-xl font-bold text-green-700">{presentToday}</p>
        </div>
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <p className="text-xs text-yellow-600">Late Today</p>
          </div>
          <p className="mt-1 text-xl font-bold text-yellow-700">{lateToday}</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <p className="text-xs text-red-600">Absent Today</p>
          </div>
          <p className="mt-1 text-xl font-bold text-red-700">{absentToday}</p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <p className="text-xs text-blue-600">Total Records</p>
          </div>
          <p className="mt-1 text-xl font-bold text-blue-700">{records.length}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          {filterDate && (
            <button onClick={() => setFilterDate('')} className="text-xs text-blue-600 hover:underline">Clear</button>
          )}
        </div>
        <Button onClick={() => setShowAdd(true)} icon={<Plus className="h-4 w-4" />}>
          Record Attendance
        </Button>
      </div>

      {/* Records Table */}
      <Card>
        <CardHeader title="Attendance Records" description={`${filtered.length} entries`} />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Employee', 'Check-In', 'Check-Out', 'Hours', 'OT', 'Status', 'Source'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">No attendance records yet.</td></tr>
              ) : filtered.map((r: AttendanceEntry) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 text-gray-900">{r.date}</td>
                  <td className="px-3 py-2.5 font-medium text-gray-900">{r.employeeName || r.employeeId?.slice(0, 8)}</td>
                  <td className="px-3 py-2.5 font-mono text-gray-600">{r.checkIn}</td>
                  <td className="px-3 py-2.5 font-mono text-gray-600">{r.checkOut}</td>
                  <td className="px-3 py-2.5 text-gray-700">{r.hoursWorked.toFixed(1)}h</td>
                  <td className="px-3 py-2.5 text-orange-600">{r.overtimeHours > 0 ? `${r.overtimeHours.toFixed(1)}h` : '—'}</td>
                  <td className="px-3 py-2.5">
                    <Badge color={(STATUS_COLORS[r.status] ?? 'gray') as any}>{r.status.replace('_', ' ')}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500 capitalize">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Record Attendance" size="md"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} icon={<Clock className="h-4 w-4" />}>Save Record</Button>
          </div>
        }>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <select value={form.employeeId} onChange={e => {
              const emp = employees.find((x: any) => x.id === e.target.value)
              setForm({ ...form, employeeId: e.target.value, employeeName: emp?.fullName ?? emp?.name ?? '' })
            }} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select employee...</option>
              {employees.map((e: any) => (
                <option key={e.id} value={e.id}>{e.fullName ?? e.name} — {e.department ?? ''}</option>
              ))}
            </select>
          </div>
          <Input label="Date" type="date" value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {['present', 'late', 'absent', 'half_day', 'on_leave'].map(s => (
                <option key={s} value={s}>{s.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <Input label="Check-In" type="time" value={form.checkIn}
            onChange={e => setForm({ ...form, checkIn: e.target.value })} />
          <Input label="Check-Out" type="time" value={form.checkOut}
            onChange={e => setForm({ ...form, checkOut: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {['biometric', 'mobile', 'manual'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <Input label="Notes" value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." />
        </div>
      </Modal>
    </div>
  )
}
